import { slugify } from './index'

// map over a function that creates arrays and concatenate the results
//
// func(a -> array of b) => array of a => array of b

const flatMap = fx => xs => Array.prototype.concat(...xs.map(fx))

// flatten out a recursive retailCategory while preserving a reference to the
// original store that contained it.
const storeCategories = storeID => (
  { parentCategory, ...retailCategory },
  categories = []
) => {
  const isIterable = categories && typeof categories === 'object'
  const spreadCategories = isIterable ? categories : []

  const category = {
    ...retailCategory,
    storeID,
    slug: slugify(retailCategory.title),
  }

  return parentCategory
    ? storeCategories(storeID)(parentCategory, [
        { ...category, parentCategory: parentCategory.title },
        ...spreadCategories,
      ])
    : [category, ...spreadCategories] // end of recursion
}

// assign items according to an indexer (a => string)
// grouped through a grouper that allows caller to combine old and new
const groupBy = (indexer, grouper) => collection =>
  collection.reduce((grouped, item) => {
    const index = indexer(item)
    grouped[index] = grouper(item, grouped[index])
    return grouped
  }, {})

// turn duplicate categories-per-store into grouped categories with a set
// of storeIDs assigned to each
const groupCategories = groupBy(
  item => item.slug,
  ({ storeID, ...item }, existing = null) =>
    existing
      ? { ...existing, storeIDs: [...existing.storeIDs, storeID] }
      : { ...item, storeIDs: [storeID] }
)

export const getRetailCategories = stores => {
  // combines deeply nested store or retailer categories into a flat lookup:
  //
  // { title: { title, level, children: [..], storeIDs }
  const groupedCategories = groupCategories(
    flatMap(store =>
      flatMap(storeCategories(store.sys.id))(
        store.retailCategories ||
          (store.retailer && store.retailer.retailCategories) ||
          []
      )
    )(stores)
  )

  // eslint-disable-next-line compat/compat
  const categories = Object.values(groupedCategories).sort((catA, catB) =>
    catA.title.localeCompare(catB.title)
  )

  const children = category =>
    categories.filter(other => other.parentCategory === category.title)

  const parent = category =>
    category.parentCategory &&
    categories.find(other => other.title === category.parentCategory)

  // all parents of this category, ordered from root ancestor down
  const parents = category =>
    (p => (p ? [...parents(p), p] : []))(parent(category))

  // recursively find all subcategories for this category.
  //
  // traversed in a "preorder" search order so that subcategories follow the
  // parent category in UI
  const subcategories = category =>
    (cs =>
      cs.length ? flatMap(child => [child, ...subcategories(child)])(cs) : [])(
      children(category)
    )

  // accessors over these data structures for UI to use
  return {
    get categories() {
      return categories
    },

    get grouped() {
      return groupedCategories
    },

    // root level categories
    get root() {
      return categories.filter(category => category.level === 1)
    },

    exists(slug) {
      return Boolean(groupedCategories[slug])
    },

    // safe access to children
    childrenOf(slug) {
      const category = groupedCategories[slug]
      return category ? children(category) : []
    },

    parentsOf(slug) {
      const category = groupedCategories[slug]
      return category ? parents(category) : []
    },

    subcategoriesOf(slug) {
      const category = groupedCategories[slug]
      return category ? subcategories(category) : []
    },

    pathOf(slug) {
      const category = groupedCategories[slug]
      return category
        ? [...parents(category).map(c => c.slug), slug].join('/')
        : null
    },

    // safe access to stores
    storeIDsOf(slug) {
      const category = groupedCategories[slug]
      return category ? category.storeIDs : []
    },
  }
}
